// TypeScript
import { useMemo } from 'react'
import { CatalogItemType, getWrappedMacro, Macro } from '@pushly/models/lib/enums'
import { getStrategyOptionsForCatalogItemType, type CatalogItemPickStrategy } from '@pushly/models/lib/enums/catalogs'
import {
    ActionStep,
    isSendCartNotificationActionStep,
    isSendSavedItemNotificationActionStep,
    type JourneyNode,
} from '../types/journey-nodes'
import {
    getPriorSendActionCount,
    computeCatalogItemPickStrategyOptions,
    reconcileSelectedStrategy,
} from '../selectors/catalog-item-pick-strategy'

export type ItemStrategyTemplates = {
    url: string
    title: string
    body: string
    image: string
    imageMacroTag: string
    imageMacroTagLabel: string
}

export type ItemStrategyParams = {
    action: ActionStep
    itemType?: CatalogItemType
    allNodes?: JourneyNode[]
    currentNodeId?: string
    currentSelectedStrategy?: CatalogItemPickStrategy
}

export type ItemStrategyState = {
    isCatalog: boolean
    templates: ItemStrategyTemplates
    options: CatalogItemPickStrategy[]
    selected: CatalogItemPickStrategy | undefined
}

function buildTemplatesAndBaseOptions(itemType?: CatalogItemType): {
    templates: ItemStrategyTemplates
    baseOptions: CatalogItemPickStrategy[]
} {
    const templates: ItemStrategyTemplates = {
        url: getWrappedMacro(Macro.ECOMM_ITEM_URL),
        title: getWrappedMacro(Macro.ECOMM_ITEM_NAME),
        body: getWrappedMacro(Macro.ECOMM_ITEM_DESCRIPTION, { defaultValue: '' }),
        image: getWrappedMacro(Macro.ECOMM_ITEM_IMAGE),
        imageMacroTag: Macro.ECOMM_ITEM_IMAGE,
        imageMacroTagLabel: 'Use Item Image',
    }

    const baseOptions = getStrategyOptionsForCatalogItemType(
        itemType ?? CatalogItemType.PRODUCT,
    ) as CatalogItemPickStrategy[]

    return { templates, baseOptions }
}

export function computeItemStrategyState(params: ItemStrategyParams): ItemStrategyState {
    const { action, itemType, allNodes, currentNodeId, currentSelectedStrategy } = params
    const isCatalog = isSendCartNotificationActionStep(action) || isSendSavedItemNotificationActionStep(action)

    const { templates, baseOptions } = buildTemplatesAndBaseOptions(itemType)

    if (!isCatalog) {
        return { isCatalog, templates, options: [], selected: undefined }
    }

    const priorCount = getPriorSendActionCount(allNodes ?? [], currentNodeId ?? '', action.meta?.actionIndex)
    const options = computeCatalogItemPickStrategyOptions(baseOptions, priorCount)
    const selected = reconcileSelectedStrategy(options, currentSelectedStrategy)

    return { isCatalog, templates, options, selected }
}

export function useItemStrategyOptions(params: ItemStrategyParams): ItemStrategyState {
    return useMemo(
        () => computeItemStrategyState(params),
        [
            params.action,
            params.action?.meta?.actionIndex,
            params.itemType,
            params.allNodes,
            params.currentNodeId,
            params.currentSelectedStrategy,
        ],
    )
}
